import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import { css } from '@styled-system/css'
import { use100vh } from 'react-div-100vh'
import { useClickAway } from 'react-use'

import {
  Box,
  Button,
  ButtonAnchor,
  ButtonUnstyled,
  Icon,
  Chip,
  P,
  Collapse,
  Span,
} from '@allied-solutions/affinity'
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Close01,
  Document02,
  Menu,
  MessageSquare01,
  Phone,
  QuestionSquare,
} from '@allied-solutions/affinity-react-icons/dist/standard'

import { theme } from '../theme.js'
import { useWindowWidth, useDisabledButtonMessaging } from '../hooks'
import { Logo } from './Logo'

const StyledBox = styled(Box)`
  position: static;
  border: 1px solid;
  border-color: ${props => props.theme.colors.border.defaultDark};
  border-radius: 25px;
  box-sizing: border-box;
  padding: ${props => props.theme.space['xl']};
  margin-top: ${props => props.theme.space[7]};
  cursor: pointer;

  &:first-child {
    margin-top: ${props => props.theme.space[15]};
  }
`

export const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.theme.colors.darkBlue} !important;
`

const helpBreakpoint = theme[2].replace('px', '')

const HelpButton = ({ needHelp, setNeedHelp, boxShadow, collapseRef }) => {
  return (
    <Button
      ref={collapseRef}
      variant="info"
      id="helpMenuToggle"
      size="default"
      typeStyle="bodyMedium"
      rounded
      onClick={() => setNeedHelp(needHelp => !needHelp)}
      icon={needHelp ? Close01 : ChevronDown}
      iconPos="right"
      boxShadow={boxShadow || 'info'}
      css={`
        @media print {
          display: none;
        }
      `}
    >
      <Span fontSize="16px">{needHelp ? 'Close' : 'Help'}</Span>
    </Button>
  )
}

const DesktopHelpMenu = () => (
  <Box
    pl="15"
    pb="15"
    pr="15"
    pt="11"
    width="28%"
    minWidth="398px"
    borderRight="1px solid"
    borderRightColor="gray80"
  >
    <Logo id="Global__Header__Logo__Secondary" />
    <Box
      width="10"
      height="10"
      gradient="blue"
      display="flex"
      alignItems="center"
      justifyContent="center"
      mt="26"
      borderRadius="full"
      mb="8"
    >
      <Icon src={Document02} color="white" />
    </Box>
    <P
      mb={0}
      typeStyle="titleMedium"
      textAlign="left"
      fontWeight="900"
      display="block"
    >
      Find answers to the most common questions people ask.
    </P>
    <a
      id="HelpMenu__Button--Support"
      href="/support/"
      target="_blank"
      rel="noreferrer"
      css={css({
        mt: 18,
        display: 'inline-flex',
        alignItems: 'center',
        textDecoration: 'none',
      })}
    >
      <P mb={0} typeStyle="labelMedium" textAlign="left" color="blue1000">
        Go to Support
      </P>
      <Icon src={ArrowRight} ml={1} />
    </a>
  </Box>
)

const DesktopLiveChatColumn = ({ liveChatDisabled, chatWillOpenTime }) => (
  <Box width="50%">
    <Box display="flex" justifyContent="space-between" flexDirection="column">
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection="column"
        minHeight="280px"
      >
        <Box>
          <P
            mb={0}
            typeStyle="titleLarge"
            textAlign="left"
            display="block"
            fontWeight="900"
          >
            Live Chat
          </P>
          <Chip rounded bg="teal1000">
            <P mb={0} typeStyle="labelSmall" textAlign="center" color="#FFFFFF">
              {liveChatDisabled ? 'Closed' : 'Open'}
            </P>
          </Chip>
          <P
            mb={0}
            typeStyle="bodyMedium"
            textAlign="left"
            color="gray1000"
            mt="8"
            display="block"
          >
            Mon. through Fri. <br />
            8:00 AM - 5:00 PM EST
          </P>
          <P
            mb={0}
            typeStyle="bodyMedium"
            textAlign="left"
            color="gary1000"
            mt="3"
            display="block"
          >
            Closed Sat., Sun., and holidays
          </P>
        </Box>
        <Box>
          <ButtonAnchor
            outlined
            id="HelpMenu__Button--StartChat"
            display="inline-block"
            rounded
            target="_blank"
            rel="noreferrer"
            href={process.env.GATSBY_CHAT_URL}
            disabled={liveChatDisabled}
          >
            {liveChatDisabled ? 'Chat Unavailable' : 'Start a Chat'}
          </ButtonAnchor>
        </Box>
      </Box>
      {liveChatDisabled && (
        <P
          mb={0}
          mt="4"
          display="block"
          typeStyle="bodySmall"
          textAlign="left"
          maxWidth="200px"
          id="liveChatClosedMessage"
        >
          {`Chat support will open ${chatWillOpenTime}`}
        </P>
      )}
    </Box>
  </Box>
)

const DesktopCallColumn = ({ phoneDisabled, phoneWillOpenTime }) => (
  <Box display="flex" flexDirection="column">
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      minHeight="280px"
    >
      <Box>
        <P
          mb={0}
          typeStyle="titleLarge"
          textAlign="left"
          display="block"
          fontWeight="900"
        >
          Call Us
        </P>
        <Chip rounded bg="teal1000">
          <P mb={0} typeStyle="labelSmall" textAlign="center" color="#FFFFFF">
            {phoneDisabled ? 'Closed' : 'Open'}
          </P>
        </Chip>
        <P
          mb={0}
          typeStyle="bodyMedium"
          textAlign="left"
          color="gray1000"
          mt="8"
          display="block"
        >
          Mon. through Fri. <br />
          8:00 AM - 9:00 PM EST
        </P>
        <P
          mb={0}
          typeStyle="bodyMedium"
          textAlign="left"
          color="gray1000"
          mt="3"
          display="block"
        >
          Sat. 9:00 AM - 6:00 PM EST
        </P>
        <P
          mb={0}
          typeStyle="bodyMedium"
          textAlign="left"
          color="gray1000"
          mt="3"
          display="block"
        >
          Closed Sun. and holidays
        </P>
      </Box>
      <Box>
        <ButtonAnchor
          outlined
          id="HelpMenu__Button--CallUs"
          display="inline-block"
          rounded
          target="_blank"
          rel="noreferrer"
          href="tel:(800) 998-0762"
          disabled={phoneDisabled}
        >
          (800) 998-0762
        </ButtonAnchor>
      </Box>
    </Box>
    {phoneDisabled && (
      <P
        mb={0}
        mt="4"
        display="block"
        typeStyle="bodySmall"
        textAlign="left"
        maxWidth="200px"
        id="callSupportClosedMessage"
      >
        {`Call support will open ${phoneWillOpenTime}`}
      </P>
    )}
  </Box>
)

const HelpDesktop = ({
  containerRef,
  needHelp,
  setNeedHelp,
  isControlled,
  boxShadow,
  collapseRef,
  liveChatDisabled,
  phoneDisabled,
  chatWillOpenTime,
  phoneWillOpenTime,
  rest,
}) => (
  <Box
    ref={containerRef}
    position="fixed"
    top="0"
    left="0"
    width="100%"
    css={`
      z-index: 11000;
    `}
  >
    <Box position="absolute" top="11" right="11" {...rest}>
      {(!isControlled || needHelp) && (
        <HelpButton
          needHelp={needHelp}
          setNeedHelp={setNeedHelp}
          boxShadow={boxShadow}
          collapseRef={collapseRef}
        />
      )}
    </Box>
    <Collapse
      id="HelpMenu__Collapse--wholeMenu"
      isOpen={needHelp}
      triggerRef={collapseRef}
      boxShadow={needHelp ? 'xl' : 'none'}
      borderBottomLeftRadius="4"
      borderBottomRightRadius="4"
      bg="white"
    >
      <Box width="100%" display="flex">
        <DesktopHelpMenu />
        <Box pr="15" pl="28" pt="24" pb="10" width="100%">
          <P
            mb={0}
            typeStyle="headlineMedium"
            textAlign="left"
            fontWeight="900"
          >
            We are here to help
          </P>
          <Box display="flex" mt="9">
            <DesktopLiveChatColumn
              liveChatDisabled={liveChatDisabled}
              chatWillOpenTime={chatWillOpenTime}
            />
            <DesktopCallColumn
              phoneDisabled={phoneDisabled}
              phoneWillOpenTime={phoneWillOpenTime}
            />
          </Box>
        </Box>
      </Box>
    </Collapse>
  </Box>
)

const MobileHelpFloatingControls = ({
  needHelp,
  setNeedHelp,
  isControlled,
  rest,
  collapseRef,
}) => {
  if (needHelp) {
    return (
      <Box
        position="fixed"
        top={['2rem', '2.4rem', null, null]}
        right="8"
        css={`
          z-index: 12000;
        `}
        {...rest}
      >
        <ButtonUnstyled
          id="HelpMenu__Button--closeMobile"
          type="button"
          aria-label="Close Help Menu"
          onClick={() => setNeedHelp(false)}
        >
          <Box
            width="8"
            height="8"
            borderRadius="8"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gradient="blue"
          >
            <Icon src={Close01} color="white" />
          </Box>
        </ButtonUnstyled>
      </Box>
    )
  }
  if (!isControlled) {
    return (
      <Box
        position="absolute"
        top={['22px', '6']}
        right={['17', '19']}
        width="18px"
        css={`
          z-index: 12;
          @media print {
            display: none;
          }
        `}
      >
        <ButtonUnstyled
          typeStyle="bodyMedium"
          id="helpMenuToggle"
          position="fixed"
          ref={collapseRef}
          top={['2.1rem', '2.65rem', null, null]}
          onClick={() => setNeedHelp(true)}
        >
          Help
          <Icon ml="2" src={Menu} />
        </ButtonUnstyled>
      </Box>
    )
  }
  return null
}

const MobileExpandableSection = ({
  title,
  toggleId,
  collapseId,
  isOpen,
  setOpen,
  triggerRef,
  children,
  leftIcon,
}) => {
  const toggleOpen = useCallback(() => setOpen(open => !open), [setOpen])
  return (
    <StyledBox>
      <ButtonUnstyled
        type="button"
        id={toggleId}
        display="flex"
        justifyContent="center"
        gap="xl"
        alignItems="center"
        width="100%"
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-controls={collapseId}
        onClick={toggleOpen}
      >
        <Icon src={leftIcon} color="gray" />
        <Span
          mb={0}
          typeStyle="buttonLarge"
          textAlign="center"
          fontWeight="500"
          color="text.secondary"
        >
          {title}
        </Span>
        <Icon src={isOpen ? ChevronUp : ChevronDown} color="gray" />
      </ButtonUnstyled>
      <Collapse id={collapseId} triggerRef={triggerRef} isOpen={isOpen}>
        {children}
      </Collapse>
    </StyledBox>
  )
}

const MobileLiveChatPanel = ({ liveChatDisabled, chatWillOpenTime }) => (
  <Box
    display="flex"
    flexDirection="column"
    gap="md"
    alignItems="center"
    mt="xl"
  >
    <Chip rounded bg="teal200">
      <P mb={0} typeStyle="caption" textAlign="center" color="info">
        {liveChatDisabled ? 'Closed' : 'Open'}
      </P>
    </Chip>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="center"
      color="gray1000"
      mt="md"
      display="block"
    >
      Mon. through Fri.
    </P>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="center"
      color="gray1000"
      mt="md"
      display="block"
    >
      8:00 AM - 5:00 PM EST
    </P>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="left"
      color="gray1000"
      mt="md"
      display="block"
    >
      Closed Sat., Sun., and holidays
    </P>
    <ButtonAnchor
      my="xl"
      outlined
      id="HelpMenu__Button--StartChat"
      display="inline-block"
      rounded
      target="_blank"
      rel="noreferrer"
      href={process.env.GATSBY_CHAT_URL}
      disabled={liveChatDisabled}
    >
      {liveChatDisabled ? 'Chat Unavailable' : 'Start a Chat'}
    </ButtonAnchor>
    {liveChatDisabled && (
      <P
        mb={0}
        mt="4"
        display="block"
        typeStyle="bodySmall"
        textAlign="left"
        id="liveChatClosedMessage"
      >
        {`Chat support will reopen ${chatWillOpenTime}`}
      </P>
    )}
  </Box>
)

const MobileCallPanel = ({ phoneDisabled, phoneWillOpenTime }) => (
  <Box
    display="flex"
    flexDirection="column"
    gap="md"
    alignItems="center"
    mt="xl"
  >
    <Chip rounded bg="teal200">
      <P mb={0} typeStyle="caption" textAlign="center" color="info">
        {phoneDisabled ? 'Closed' : 'Open'}
      </P>
    </Chip>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="left"
      color="gray1000"
      mt="5"
      display="block"
    >
      Mon. through Fri.
    </P>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="left"
      color="gray1000"
      mt="md"
      display="block"
    >
      8:00 AM - 9:00 PM EST
    </P>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="left"
      color="gray1000"
      mt="3"
      display="block"
    >
      Sat. 9:00 AM - 6:00 PM EST
    </P>
    <P
      mb={0}
      typeStyle="bodyMedium"
      textAlign="left"
      color="gray1000"
      mt="md"
      display="block"
    >
      Closed Sun. and holidays
    </P>
    <ButtonAnchor
      my="xl"
      outlined
      id="HelpMenu__Button--CallUs"
      display="inline-block"
      rounded
      target="_blank"
      rel="noreferrer"
      href="tel:(800) 998-0762"
      disabled={phoneDisabled}
    >
      (800) 998-0762
    </ButtonAnchor>
    {phoneDisabled && (
      <P
        mb={0}
        mt="4"
        display="block"
        typeStyle="bodySmall"
        textAlign="left"
        id="callSupportClosedMessage"
      >
        {`Call support will reopen ${phoneWillOpenTime}`}
      </P>
    )}
  </Box>
)

const HelpMobile = ({
  needHelp,
  setNeedHelp,
  isControlled,
  rest,
  collapseRef,
  menuHeight,
  liveChatOpen,
  setLiveChatOpen,
  callUsOpen,
  setCallUsOpen,
  collapseRefLiveChat,
  collapseRefCall,
  liveChatDisabled,
  phoneDisabled,
  chatWillOpenTime,
  phoneWillOpenTime,
}) => (
  <>
    <MobileHelpFloatingControls
      needHelp={needHelp}
      setNeedHelp={setNeedHelp}
      isControlled={isControlled}
      rest={rest}
      collapseRef={collapseRef}
    />
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      css={`
        z-index: 11000;
      `}
    >
      <Collapse
        id="HelpMenu__Collapse--wholeMenu"
        isOpen={needHelp}
        triggerRef={collapseRef}
      >
        <Box
          px={['6', '8']}
          pt={['2.2rem', '2.57rem', '2.56rem', 5]}
          pl={['2rem', null, null, null]}
          pb="20"
          top="0"
          left="0"
          bg="white"
          height={`${menuHeight}px`}
          overflow="auto"
          css={`
            z-index: 7;
          `}
        >
          <Logo id="Global__Header__Logo__Help" />
          <P
            mb={0}
            typeStyle="titleLarge.bold"
            textAlign="center"
            display="block"
            mt="19"
            px="7"
          >
            We are here to help
          </P>
          <P
            mb={0}
            typeStyle="bodyLarge.regular"
            color="text.secondary"
            fontWeight={400}
            lineHeight="32px"
            textAlign="center"
            mt="xl"
            display="block"
          >
            Still have questions? Don't worry, we can help. Chat with an agent
            or give us a call to get the answers you need.
          </P>
          <StyledLink to="/support/" id="HelpMenu__Button--Support">
            <StyledBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap="xl"
            >
              <Icon src={QuestionSquare} color="gray" />
              <P
                mb={0}
                typeStyle="buttonLarge"
                fontWeight="500"
                color="text.secondary"
              >
                Read FAQs
              </P>
              <Icon
                color="gray"
                src={ChevronDown}
                css={`
                  transform: rotate(-90deg);
                `}
              />
            </StyledBox>
          </StyledLink>
          <MobileExpandableSection
            title="Live Chat"
            toggleId="mobileLiveChatOpenToggle"
            collapseId="HelpMenu__Collapse--liveChat"
            isOpen={liveChatOpen}
            setOpen={setLiveChatOpen}
            triggerRef={collapseRefLiveChat}
            leftIcon={MessageSquare01}
          >
            <MobileLiveChatPanel
              liveChatDisabled={liveChatDisabled}
              chatWillOpenTime={chatWillOpenTime}
            />
          </MobileExpandableSection>
          <MobileExpandableSection
            title="Call Us"
            toggleId="mobileCallOpenToggle"
            collapseId="HelpMenu__Collapse--callUs"
            isOpen={callUsOpen}
            setOpen={setCallUsOpen}
            triggerRef={collapseRefCall}
            leftIcon={Phone}
          >
            <MobileCallPanel
              phoneDisabled={phoneDisabled}
              phoneWillOpenTime={phoneWillOpenTime}
            />
          </MobileExpandableSection>
        </Box>
      </Collapse>
    </Box>
  </>
)

const Help = ({
  boxShadow,
  needHelp: needHelpProp,
  setNeedHelp: setNeedHelpProp,
  ...rest
}) => {
  const [internalNeedHelp, setInternalNeedHelp] = useState(false)
  const isControlled =
    needHelpProp !== undefined && typeof setNeedHelpProp === 'function'
  const needHelp = isControlled ? needHelpProp : internalNeedHelp
  const setNeedHelp = isControlled ? setNeedHelpProp : setInternalNeedHelp
  const windowWidth = useWindowWidth()
  const [liveChatOpen, setLiveChatOpen] = useState(false)
  const [callUsOpen, setCallUsOpen] = useState(false)
  const {
    liveChatDisabled,
    phoneDisabled,
    chatWillOpenTime,
    phoneWillOpenTime,
  } = useDisabledButtonMessaging()
  const menuHeight = use100vh()
  const ref = useRef(null)
  const callRef = useRef(null)
  const collapseRef = useRef()
  const collapseRefLiveChat = useRef()
  const collapseRefCall = useRef()
  useClickAway(ref, () => {
    setNeedHelp(false)
  })
  useClickAway(callRef, () => {
    setCallUsOpen(false)
  })

  useEffect(() => {
    if (needHelp) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }, [needHelp])

  const isDesktop = windowWidth >= Number(helpBreakpoint)

  if (isDesktop) {
    return (
      <HelpDesktop
        containerRef={ref}
        needHelp={needHelp}
        setNeedHelp={setNeedHelp}
        isControlled={isControlled}
        boxShadow={boxShadow}
        collapseRef={collapseRef}
        liveChatDisabled={liveChatDisabled}
        phoneDisabled={phoneDisabled}
        chatWillOpenTime={chatWillOpenTime}
        phoneWillOpenTime={phoneWillOpenTime}
        rest={rest}
      />
    )
  }

  return (
    <HelpMobile
      needHelp={needHelp}
      setNeedHelp={setNeedHelp}
      isControlled={isControlled}
      rest={rest}
      collapseRef={collapseRef}
      menuHeight={menuHeight}
      liveChatOpen={liveChatOpen}
      setLiveChatOpen={setLiveChatOpen}
      callUsOpen={callUsOpen}
      setCallUsOpen={setCallUsOpen}
      collapseRefLiveChat={collapseRefLiveChat}
      collapseRefCall={collapseRefCall}
      liveChatDisabled={liveChatDisabled}
      phoneDisabled={phoneDisabled}
      chatWillOpenTime={chatWillOpenTime}
      phoneWillOpenTime={phoneWillOpenTime}
    />
  )
}

export { Help }
